import { EnvironmentProviders, InjectionToken, Provider } from '@angular/core';
import { UserInterface } from '@techniek-team/common';
import {
  OidcSecurityService,
  OpenIdConfiguration,
  provideAuth,
  StsConfigLoader,
  StsConfigStaticLoader,
  withAppInitializerAuthCheck,
} from 'angular-auth-oidc-client';
import { OidcOauthConfig, OidcOauthInputConfig } from './config/oidc-oauth.config';
import { LogoutService } from './service/logout/logout.service';
import { OAuthService } from './service/oauth/oauth.service';
import { UserService } from './service/user/user.service';
import { configDefaults } from './config/oidc-oauth-defaults.config';

export const TT_AUTH_CONFIGS = new InjectionToken<
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  OidcOauthConfig<any>[]
>('auth.configs');

export const WITH_POPUP = new InjectionToken<boolean>('auth.withPopup');

export function provideTtAuth<T extends UserInterface>(
  options: OidcOauthInputConfig<T>,
  ...features: (
    | (Provider | EnvironmentProviders)[]
    | { options: Partial<OidcOauthInputConfig<T>>; providers: (Provider | EnvironmentProviders)[] }
    | {
        options: () => Partial<OidcOauthInputConfig<T>>;
        providers: (Provider | EnvironmentProviders)[];
      }
  )[]
) {
  const config = [{ ...configDefaults, ...options }] as OidcOauthConfig<T>[];
  const providers = features.map((feature) =>
    typeof feature === 'object' && 'providers' in feature ? feature.providers : feature,
  );

  return [
    provideAuth(
      {
        config: config as OpenIdConfiguration[],
        loader: {
          provide: StsConfigLoader,
          useFactory: () => {
            for (const feature of features) {
              if (!(typeof feature === 'object' && 'options' in feature)) {
                continue;
              }
              config[0] = {
                ...config[0],
                ...(typeof feature.options === 'function' ? feature.options() : feature.options),
              };
            }
            return new StsConfigStaticLoader(config);
          },
        },
      },
      withAppInitializerAuthCheck(),
    ),
    { provide: TT_AUTH_CONFIGS, useFactory: retrieveConfig(config), deps: [OidcSecurityService] },
    LogoutService,
    OAuthService,
    UserService,
    ...providers,
  ];
}

function retrieveConfig<T extends UserInterface>(
  config: OidcOauthConfig<T> | OidcOauthConfig<T>[],
) {
  return (oidcSecurityService: OidcSecurityService) => {
    const configurations = oidcSecurityService.getConfigurations();
    if (Array.isArray(config)) {
      return config.map((foundConfig) => ({
        ...foundConfig,
        ...(configurations.find((item) => item.configId === foundConfig.configId) ?? {}),
      }));
    }
    return [
      { ...config, ...(configurations.find((item) => item.configId === config.configId) ?? {}) },
    ];
  };
}
